import React, { createContext, useContext } from "react";
import {
  Device,
  ManagementInterface,
  ManagementProtocol,
} from "@services/devices/types";
import {
  DevicePropertyErrors,
  FieldError,
  ManagementInterfacePropertyKey,
} from "@common/utils/devices/validation";
import { BackendDeviceValidation } from "@services/devices";
import {
  FlattenedDevice,
  FlattenedDeviceWithIndex,
  IndexedDevice,
} from "./const";
import { MappingField } from "../Devices/MappingColumnModal";
import { IImportMode, ProvisioningFilters } from "../common/ImportListContext";

export interface BreadcrumbProvisionningProps {
  previous: string;
  title?: string;
  label?: string;
  referrer?: string;
}

// export interface IDeviceWithMapping extends Device {
//   mapped?: boolean;
// }
export interface IDeviceProvisioningContext {
  devices: Device[];
  open: boolean | Required<Device>["id"] | Array<Required<Device>["id"]>;
  breadcrumb: BreadcrumbProvisionningProps;
  excelImport?: boolean;
  excelHeaders?: string[];
  excelRawData?: string[][];
  onConfirm: () => void;
  mapping?: MappingField[];
}

export type OpenWithDataProps = {
  excelFieldsList: string[];
  rawData: string[][];
  mapping?: MappingField[];
  manualImport?: boolean;
};

export interface IDeviceProvisioningDispatchContext {
  setDevices: (devices: IDeviceProvisioningContext["devices"]) => void;
  setOpen: (open: IDeviceProvisioningContext["open"]) => void;
  openWithData: (data: OpenWithDataProps, importMode?: IImportMode) => void;
  saveMapping?: () => void;
}

export const DeviceProvisioningContext =
  createContext<IDeviceProvisioningContext>({
    devices: [],
    open: false,
    breadcrumb: { previous: "" },
    excelImport: false,
    onConfirm: () => {},
  });

export const DeviceProvisioningDispatchContext =
  createContext<IDeviceProvisioningDispatchContext>({
    setDevices: () => {},
    setOpen: () => {},
    openWithData: () => {},
  });

export type IdentifiedMappingField = Partial<Omit<MappingField, "key">> & {
  key: keyof FlattenedDeviceWithIndex;
  id: string;
};

// Used to manage the fact that two columns can have the same "key" from the device, but still have different values stored within
export type DataCellRetention = {
  [k in IdentifiedMappingField["id"]]: {
    key: keyof FlattenedDeviceWithIndex;
    data: {
      [l in IndexedDevice["index"]]: any;
    };
  };
};
export type DataRetentionEntry<K extends keyof FlattenedDeviceWithIndex> = {
  value: FlattenedDeviceWithIndex[K];
} & Pick<IdentifiedMappingField, "key" | "id">;
export interface IDeviceProvisioningListContext {
  devices: IndexedDevice[];
  selected: Array<IndexedDevice["index"]>;
  backendErrors: { [k in IndexedDevice["index"]]: FieldError };
  filter?: ProvisioningFilters;
  displayMapping: boolean;
  displayBulkRow: boolean;
  showUnmapped: boolean;
  mapping?: IdentifiedMappingField[];
  //Used to retain information that can change which field it's applied to
  floatingData: DataCellRetention;
  page: number;
  importMode?: IImportMode;
  /**
   * Having these two booleans as purely computed values to have more
   * readable conditions throughout the code i.e.
   * `if(manualImport)` instead of `if(importMode === ImportMode.MANUAL)`
   */
  manualImport: boolean;
  bulkUpload: boolean;
}
export interface IDeviceProvisioningListDispatchContext {
  setPage: (page: number) => void;
  setMapping: (
    identifier: string,
    colKey: keyof FlattenedDeviceWithIndex,
    excelField?: string
  ) => void;
  addDevices: (devices: Device[]) => void;
  removeDevices: () => void;
  setDisplayMapping: (b: boolean) => void;
  setShowUnmapped: (b: boolean) => void;
  setDisplayBulkRow: (b: boolean) => void;
  setFilter: (f: ProvisioningFilters | undefined) => void;
  setSelected: (s: IDeviceProvisioningListContext["selected"]) => void;
  setValue: <K extends keyof FlattenedDeviceWithIndex>(
    device: IndexedDevice["index"],
    compositeKey: K,
    value: FlattenedDeviceWithIndex[K],
    dataEntry?: DataRetentionEntry<K>
  ) => void;
  setValues: <K extends keyof FlattenedDeviceWithIndex>(
    device: IndexedDevice["index"],
    changes: Partial<FlattenedDevice>,
    dataEntry?: DataRetentionEntry<K>
  ) => void;
  bulkChange: <K extends keyof FlattenedDeviceWithIndex>(
    devices: Array<IndexedDevice["index"]>,
    changes: Partial<FlattenedDevice>,
    dataEntry?: DataRetentionEntry<K>
  ) => void;
  getErrors: (
    device: IndexedDevice["index"],
    property: keyof FlattenedDeviceWithIndex
  ) => FieldError[];
  isRequired: (
    device: IndexedDevice["index"],
    property: keyof FlattenedDeviceWithIndex
  ) => boolean;
  getManagementInterfaceProperty: <K extends ManagementInterfacePropertyKey>(
    device: IndexedDevice["index"],
    protocol: ManagementProtocol,
    key: K
  ) => ManagementInterface[K] | undefined;
  setBackendErrors: (errors: BackendDeviceValidation[]) => void;
  setMappings: (newMapping: MappingField[]) => void;
  setIdentifiedMappings: (mapping: IdentifiedMappingField[]) => void;
}

export const useDeviceListWithFilter = (f: ProvisioningFilters | undefined) => {
  const { devices } = useContext(DeviceProvisioningListContext);
  const { getErrors } = useContext(DeviceProvisioningListDispatchContext);
  return devices.filter(({ index }) => {
    const errors = getErrors(index, "index");
    switch (f) {
      case ProvisioningFilters.EMPTY_REQUIRED:
        return errors.some(
          (error) =>
            error.error === DevicePropertyErrors.REQUIRED_FIELD_IS_EMPTY
        );
      case ProvisioningFilters.INVALID_DATA:
        return (
          errors.length > 0 &&
          errors.some(
            (error) =>
              error.error !== DevicePropertyErrors.REQUIRED_FIELD_IS_EMPTY
          )
        );
      default:
        return true;
    }
  });
};

export const useDeviceListCounters = () => {
  const { devices } = useContext(DeviceProvisioningListContext);
  const { getErrors } = useContext(DeviceProvisioningListDispatchContext);

  let [invalid, emptyRequired, valid] = [0, 0, 0];
  devices.forEach(({ index }) => {
    const errors = getErrors(index, "index");
    if (errors.length === 0) {
      valid++;
      return;
    }
    if (
      errors.some(
        (error) => error.error === DevicePropertyErrors.REQUIRED_FIELD_IS_EMPTY
      )
    )
      emptyRequired++;
    if (
      errors.some(
        (error) => error.error !== DevicePropertyErrors.REQUIRED_FIELD_IS_EMPTY
      )
    )
      invalid++;
  });
  return { invalid, emptyRequired, valid };
};
export const useFilteredDeviceList = () => {
  const { filter } = useContext(DeviceProvisioningListContext);
  return useDeviceListWithFilter(filter);
};

export const DeviceProvisioningListContext =
  createContext<IDeviceProvisioningListContext>({
    devices: [],
    selected: [],
    backendErrors: {},
    displayMapping: false,
    showUnmapped: false,
    displayBulkRow: false,
    floatingData: {},
    page: 1,
    manualImport: false,
    bulkUpload: false,
  });

export const DeviceProvisioningListDispatchContext =
  createContext<IDeviceProvisioningListDispatchContext>({
    setPage: () => {},
    setMapping: () => {},
    setDisplayMapping: () => {},
    setShowUnmapped: () => {},
    setDisplayBulkRow: () => {},
    addDevices: () => {},
    removeDevices: () => {},
    setFilter: () => {},
    setSelected: () => {},
    setValue: () => {},
    setValues: () => {},
    bulkChange: () => {},
    getErrors: () => [],
    isRequired: () => false,
    setBackendErrors: () => {},
    getManagementInterfaceProperty: () => undefined,
    setMappings: () => {},
    setIdentifiedMappings: () => {},
  });
